import * as i0 from '@angular/core';
import { Injectable, InjectionToken, NgModule } from '@angular/core';
import { BQXConfiguration, BqxUiCommonModule } from 'bqx-ui-common';
import * as _ from 'lodash';
import { ReplaySubject, Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as i1 from 'ngx-cookie-service';
import * as AWS from 'aws-sdk';
import jwt_decode from 'jwt-decode';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

class BqxAuthClientAbstractService {
}

/**
 * @class Credentials
 * @description Generic Class to Instantiate AWS Identity Result Set or Default token result set
 */
class Credentials {
    constructor(val) {
        this.data = val;
    }
}
/**
 * @class ClientOAuth2Token
 * @description OAuth2 Result Set Type Declaration
 */
class ClientOAuth2Token {
}
/**
 * @class ClientOAuth2TokenData
 * @description OAuth2 Token Type Declaration
 */
class ClientOAuth2TokenData {
}

/**
 * @class IAuthCookieService
 * @description Generic Cookie service to implement Auth cookie get/set logic.
 * @member cookieKey  write only property to set Auth cookie key (Name)
 * @member authCookie$  read only property get Auth cookie from browser of Observable<any | null> type
 * @member isCookieCredentialsExpired  boolean
 * @method requestAuthCookie() request existing Auth cookie stored in browser
 * @method clearAuthCookie() Clear existing Auth cookie stored in browser
 * @method setAuthCookie() set Auth cookie to store in browser
 */
class AwsAuthCookieService {
    set cookieKey(value) {
        this._cookieKey = value;
    }
    get authCookie$() {
        return this._credentialsSubject$.asObservable();
    }
    get isCookieCredentialsExpired() {
        return this.cookieService.get(this._cookieKey) === '' ? true : false;
    }
    constructor(cookieService) {
        this.cookieService = cookieService;
        this._credentialsSubject$ = new ReplaySubject(1);
        this._cookieKey = 'app-credentials';
    }
    requestAuthCookie() {
        this.cookieService.check(this._cookieKey)
            ? this._credentialsSubject$.next(JSON.parse(this.cookieService.get(this._cookieKey)))
            : this._credentialsSubject$.next(null);
    }
    clearAuthCookie() {
        this.cookieService.delete(this._cookieKey, '/', '', false, 'Strict'); // Cookies must be deleted with the same parameters as they were set with
        this._credentialsSubject$.next(null);
    }
    setAuthCookie(value) {
        this.cookieService.set(this._cookieKey, JSON.stringify(value), value.expiration, '/', '', false, 'Strict');
        this.requestAuthCookie();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AwsAuthCookieService, deps: [{ token: i1.CookieService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AwsAuthCookieService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AwsAuthCookieService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.CookieService }] });

/**
 * @class AuthUrlTokenService
 * @description Implicite Flow Authentication Token Service.
 * @member credentials$  Observable<IAwsIdentityCredentialResult | null>
 * @member token$  Observable<string | null>
 * @member accesstoken  string | null;
 * @method clearToken() Clear buffer token
 * @method getAuthCredentials() Parse Credential from Token and assign to IAwsIdentityCredentialResult subject
 * @method getAuthToken() get token by any Auth Implicite Flow
 */
class AuthUrlTokenService {
    get credentials$() {
        return this._credentialsSubject$.asObservable();
    }
    get token$() {
        return this._urlTokenSubject$.asObservable();
    }
    get accesstoken() {
        return this._accessToken;
    }
    constructor() {
        this._urlTokenSubject$ = new ReplaySubject(1);
        this._credentialsSubject$ = new ReplaySubject(1);
        this.clearCredentials();
        this.requestToken();
    }
    clearToken() {
        this._urlTokenSubject$.next(null);
    }
    clearCredentials(isURLToken) {
        this._credentialsSubject$.next(null);
        if (isURLToken)
            this.redirectTologout();
    }
    requestToken() {
        this._urlTokenSubject$.next(this.extractToken());
    }
    extractToken() {
        const tokenRegEx = new RegExp('(?:#access_token=)(.+)(?:&id_token=)');
        const location = window.location;
        if (location) {
            const match = location.hash.match(tokenRegEx);
            const tokens = location.hash.split('&');
            if (match) {
                const access_token = tokens[0].split('access_token=')[1];
                this._accessToken = access_token;
                return tokens[1].split('id_token=')[1];
            }
        }
        return null;
    }
    getAuthToken() {
        const authorizeUri = BQXConfiguration.data.authUri +
            '/oauth2/authorize/?response_type=token&client_id=' +
            BQXConfiguration.data.authAppClientId +
            '&redirect_uri=' +
            BQXConfiguration.data.authRedirectUri +
            '&state=STATE';
        window.location?.replace(authorizeUri);
    }
    getAuthCredentials(token) {
        AWS.config.update({ region: BQXConfiguration.data.authRegion });
        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();
        const params = {
            AccessToken: this._accessToken /* required */
        };
        cognitoidentityserviceprovider.getUser(params, (err, data) => {
            if (err) {
                console.log(err);
            }
            else {
                localStorage.setItem('client-user', JSON.stringify(data));
                this.getToken(token, this.getRolefromUserData(data)).subscribe((credentials) => {
                    this._credentialsSubject$.next(credentials);
                });
            }
        });
    }
    getRolefromUserData(userData) {
        let temRole = '';
        userData.UserAttributes.forEach((attribute) => {
            if (attribute.Name == 'profile') {
                if (!attribute.Value.includes(',')) {
                    attribute.Value = '[' + attribute.Value + ']';
                }
                temRole = attribute.Value.split('[')[1].split(']')[0].split(',')[0];
            }
        });
        return temRole;
    }
    getToken(urlToken, role) {
        const cognitoidentity = new AWS.CognitoIdentityCredentials({
            IdentityPoolId: BQXConfiguration.data.authRegion + ':' + BQXConfiguration.data.authPoolIdentityId[role],
            Logins: { ['cognito-idp.' + BQXConfiguration.data.authRegion + '.amazonaws.com/' + BQXConfiguration.data.authUserPoolId]: urlToken }
        }, { region: BQXConfiguration.data.authRegion });
        // Observable's value will be the token from the API call
        return new Observable((subscriber) => {
            cognitoidentity
                .getPromise()
                .then(() => {
                const expireTime = new Date(jwt_decode(this.accesstoken).exp * 1000);
                const credentials = {
                    // clientCredentials: cognitoidentity
                    accessKeyId: cognitoidentity.accessKeyId,
                    secretAccessKey: cognitoidentity.secretAccessKey,
                    sessionToken: cognitoidentity?.sessionToken,
                    accessToken: this.accesstoken,
                    expiration: expireTime
                };
                subscriber.next(credentials);
                subscriber.complete();
            })
                .catch((err) => {
                subscriber.error(err);
                subscriber.complete();
            });
        });
    }
    redirectTologout() {
        localStorage.clear();
        //TODO : Logging for nul check;
        const logoutUrl = BQXConfiguration.data.authUri +
            '/logout?client_id=' +
            BQXConfiguration.data.authAppClientId +
            '&logout_uri=' +
            BQXConfiguration.data.authLogoutUri;
        window.location?.replace(logoutUrl);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AuthUrlTokenService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AuthUrlTokenService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AuthUrlTokenService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

/**
 * @class AwsAzureClientCredentials
 * @description Azure Client Credential Type for SSO authentication
 * @member credentials$  Observable<IAwsIdentityCredentialResult | null>
 * @member isCookieCredentialsExpired  boolean
 * @method initClient() to Initialize actaul Auth Url token service and set subscription for token result
 * @method refreshCredentials() Clear stored token and credentials
 */
class AwsAzureClientCredentials {
    get isCookieCredentialsExpired() {
        return this.authCookieService.isCookieCredentialsExpired;
    }
    constructor(cookieService) {
        this.cookieService = cookieService;
        this.unsubscribeSubject$ = new Subject();
        this.authCookieService = new AwsAuthCookieService(this.cookieService);
        this.authCookieService.cookieKey = BQXConfiguration.data.authCookieKey;
        this.authTokenService = new AuthUrlTokenService();
        this.initClient();
    }
    ngOnDestroy() {
        this.unsubscribeSubject$.next(null);
        this.unsubscribeSubject$.complete();
    }
    initClient() {
        this.credentials$ = this.authCookieService.authCookie$.pipe(filter((x) => !!x), map((x) => new Credentials(x).data));
        this.authCookieService.requestAuthCookie();
        combineLatest([this.authTokenService.token$, this.authCookieService.authCookie$, this.authTokenService.credentials$])
            .pipe(distinctUntilChanged(_.isEqual), takeUntil(this.unsubscribeSubject$))
            .subscribe(([urlToken, cookieCredentials, retrievedCredentials]) => {
            // if credentials don't exist in the browser cookies, need to authenticate
            if (!cookieCredentials) {
                if (!urlToken && !retrievedCredentials) {
                    // if a token is not present in the url, need to redirect to cognito hosted ui
                    this.authTokenService.getAuthToken();
                }
                else if (urlToken && !retrievedCredentials) {
                    // if a token is present in the url, need to authenticate with cognito identity pool
                    this.authTokenService.getAuthCredentials(urlToken);
                }
                else if (retrievedCredentials) {
                    // if credentials have been retrieved with cognito, need to store in browser cookies
                    this.setCredentials(retrievedCredentials);
                    this.authTokenService.clearToken();
                }
            }
        }, (err) => {
            console.log(err);
            this.unsubscribeSubject$.next(null);
            this.unsubscribeSubject$.complete();
        });
    }
    setCredentials(credentials) {
        this.authCookieService.setAuthCookie(credentials);
    }
    refreshCredentials() {
        this.authCookieService.clearAuthCookie();
        this.authTokenService.clearCredentials();
    }
    logout() {
        this.unsubscribeSubject$.next();
        this.unsubscribeSubject$.complete();
        this.authCookieService.clearAuthCookie();
        this.authTokenService.clearCredentials(true);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AwsAzureClientCredentials, deps: [{ token: i1.CookieService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AwsAzureClientCredentials, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: AwsAzureClientCredentials, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.CookieService }] });

const APP_CONFIG = new InjectionToken('app.config');
class BqxAuthClientService {
    get authClient() {
        switch (BQXConfiguration.data.authProvider) {
            case 'AZURE': {
                this._client = new AwsAzureClientCredentials(this.cookieService);
                break;
            }
            default: {
                throw new Error('No implementation found for the provider');
            }
        }
        return this._client;
    }
    constructor(cookieService) {
        this.cookieService = cookieService;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxAuthClientService, deps: [{ token: i1.CookieService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxAuthClientService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxAuthClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.CookieService }] });

class BqxAuthModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.8", ngImport: i0, type: BqxAuthModule, imports: [BqxUiCommonModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxAuthModule, providers: [
            provideHttpClient(withInterceptorsFromDi())
        ], imports: [BqxUiCommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxAuthModule, decorators: [{
            type: NgModule,
            args: [{ declarations: [],
                    exports: [], imports: [BqxUiCommonModule], providers: [
                        provideHttpClient(withInterceptorsFromDi())
                    ] }]
        }] });

/*
 * Public API Surface of bqx-auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APP_CONFIG, BqxAuthClientAbstractService, BqxAuthClientService, BqxAuthModule };
